import { QueryClient } from "@tanstack/react-query";
import { CommentType, FeedNames, FeedType, PostType } from "./memeverse_types";
import CONFIG from "../../config/config";

const enableDevTools = process.env.REACT_APP_ENABLE_DEVTOOLS === 'true';

export const flattenCommentsList = (
  comments: Record<string, CommentType>,
  comments_list: CommentType[],
  page_no: number,
  visited: Record<string, boolean> = {}
) => {
  let flatList = [] as string[];
  for (let comment of comments_list) {
    if (!visited[comment.docId]) {
      visited[comment.docId] = true;
      comment.page_no = page_no;
      comments[comment.docId] = comment;
      flatList.push(comment.docId)
      if (comment.comments_list) {
        let flatList = flattenCommentsList(
          comments,
          comment.comments_list,
          page_no,
          visited
        )
        comment.flat_comments_list = flatList;
      }
    }
  }
  return flatList;
}

// mutates original list
const filterListById = (id: string, list: string[]) => {
  if (!id || !list || !list.length) {
    return;
  }

  let shouldRemove = (id2: string) => {
    return id2 === id;
  }

  for (let jj = list.length-1; jj >= 0; jj--) {
    if (shouldRemove(list[jj])) {
      list.splice(jj,1);
    }
  }
}

/////////////////////////////// Meme Posts /////////////////////////////////////
export const addNewPostToQueryData = (
  newPost: PostType,
  queryClient: QueryClient,
  feedName: FeedNames,
) => {
  let hasMutatedData = false;
  queryClient.setQueryData(
    feedName,
    (oldData: FeedType) => {
      let targetFeed = 'memesCommunity';

      if (newPost.user_id === (CONFIG.SOOPRA_NEWS_ID || 'LvOfCYkJxYlLfT8hQmlK')) {
        targetFeed = 'memesNews';
      }
      if (feedName[0] !== targetFeed) {
        return oldData;
      }

      if (enableDevTools) console.log('Creating post ', newPost.docId)
      // only mutate data on main feed page
      let newData: FeedType = oldData;
      newData.pages[0].posts[newPost.docId] = newPost;
      newData.pages[0].orderedPosts.unshift(newPost.docId);

      hasMutatedData = true;
      return newData;
    }
  )
  return hasMutatedData;
}

export const updatePostInQueryData = (
  newPost: PostType,
  queryClient: QueryClient,
  feedName: FeedNames,
) => {
  let hasMutatedData = false;
  queryClient.setQueryData(
    feedName,
    (oldData: FeedType) => {

      for (let ii = 0; ii < oldData.pages.length; ii++) {
        if (oldData.pages[ii].posts[newPost.docId]) {
          // found the post to update
          if (enableDevTools) console.log('Updating post ', newPost.docId)
          let newData: FeedType = oldData;

          let updatedPost = oldData.pages[ii].posts[newPost.docId];
          updatedPost.content = newPost.content;
          updatedPost.updatedAt = newPost.updatedAt;
          newData.pages[ii].posts[newPost.docId] = updatedPost;
          hasMutatedData = true;
          return newData;
        }
      }

      // updated post is not in feed, skip update
      return oldData;
    }
  )
  return hasMutatedData;
}

export const deletePostFromQueryData = (
  docId: string,
  queryClient: QueryClient,
  feedName: FeedNames,
) => {
  let hasMutatedData = false;
  queryClient.setQueryData(
    feedName,
    (oldData: FeedType) => {

      for (let ii = 0; ii < oldData.pages.length; ii++) {
        if (oldData.pages[ii].posts[docId]) {
          // found the post to update
          if (enableDevTools) console.log('Deleting post ', docId)
          let newData: FeedType = oldData;

          newData.pages[ii].orderedPosts =
            newData.pages[ii].orderedPosts.filter(id => id !== docId);
          delete newData.pages[ii].posts[docId];
          // no need to delete child comments as they wont be able to render anyway

          hasMutatedData = true;
          return newData;
        }
      }

      return oldData;
    }
  )
  return hasMutatedData;
}


///////////////////////////// Meme Comments ////////////////////////////////////
export const addNewCommentInQueryData = (
  newCommentData: CommentType,
  queryClient: QueryClient,
  feedName: FeedNames,
) => {
  let hasMutatedData = false;
  queryClient.setQueryData(
    feedName,
    (oldData: FeedType) => {
      // identify what kind of parent the comment has
      if (newCommentData.parent_reply_id) {
        // parent is a comment, look for it:
        for (let ii = 0; ii < oldData.pages.length; ii++) {
          if (oldData.pages[ii].comments[newCommentData.parent_reply_id]) {
            // found the parent:
            if (enableDevTools) console.log('Creating reply to reply', newCommentData.parent_reply_id)
            let newData: FeedType = oldData;
            newData.pages[ii].comments[newCommentData.parent_reply_id].reply_counts ||= 0;
            newData.pages[ii].comments[newCommentData.parent_reply_id].reply_counts++;
            newData.pages[ii].comments[newCommentData.parent_reply_id].flat_comments_list ||= [];
            newData.pages[ii].comments[newCommentData.parent_reply_id].flat_comments_list.push(newCommentData.docId)
            newData.pages[ii].comments[newCommentData.docId] = newCommentData;

            // Also try removing thinking animation if it exists:
            let fcl = newData.pages[ii].comments[newCommentData.parent_reply_id].flat_comments_list;
            let thinkingId = `thinking-${newCommentData.user_id}-${newCommentData.parent_reply_id}`
            filterListById(thinkingId, fcl)

            delete newData.pages[ii].comments[thinkingId]

            hasMutatedData = true;
            return newData;
          }
        }
      } else if (newCommentData.parent_comment_id) {
        // parent is a comment, look for it:
        for (let ii = 0; ii < oldData.pages.length; ii++) {
          if (oldData.pages[ii].comments[newCommentData.parent_comment_id]) {
            // found the parent:
            if (enableDevTools) console.log('Creating reply to comment', newCommentData.parent_comment_id)
            let newData: FeedType = oldData;
            newData.pages[ii].comments[newCommentData.parent_comment_id].reply_counts ||= 0;
            newData.pages[ii].comments[newCommentData.parent_comment_id].reply_counts++;
            newData.pages[ii].comments[newCommentData.parent_comment_id].flat_comments_list ||= [];
            newData.pages[ii].comments[newCommentData.parent_comment_id].flat_comments_list.push(newCommentData.docId)
            newData.pages[ii].comments[newCommentData.docId] = newCommentData;

            // Also try removing thinking animation if it exists:
            let fcl = newData.pages[ii].comments[newCommentData.parent_comment_id].flat_comments_list;
            let thinkingId = `thinking-${newCommentData.user_id}-${newCommentData.parent_comment_id}`;
            filterListById(thinkingId, fcl)

            delete newData.pages[ii].comments[thinkingId]

            hasMutatedData = true;
            return newData;
          }
        }
      } else {
        // parent is a post, look for it:
        for (let ii = 0; ii < oldData.pages.length; ii++) {
          if (oldData.pages[ii].posts[newCommentData.parent_post_id]) {
            // found the parent:
            if (enableDevTools) console.log('Creating comment on post', newCommentData.parent_post_id)
            let newData: FeedType = oldData;
            newData.pages[ii].posts[newCommentData.parent_post_id].comments_counts ||= 0;
            newData.pages[ii].posts[newCommentData.parent_post_id].comments_counts++;
            newData.pages[ii].posts[newCommentData.parent_post_id].flat_comments_list ||= [];
            newData.pages[ii].posts[newCommentData.parent_post_id].flat_comments_list.push(newCommentData.docId)
            newData.pages[ii].comments[newCommentData.docId] = newCommentData;

            if (oldData.pages[ii].orderedComments) {
              // should update orderedComments where necessary
              newData.pages[ii].orderedComments.push(newCommentData.docId)
            }

            // Also try removing thinking animation if it exists:
            let fcl = newData.pages[ii].posts[newCommentData.parent_post_id].flat_comments_list;
            let thinkingId = `thinking-${newCommentData.user_id}-${newCommentData.parent_post_id}`
            filterListById(thinkingId, fcl)

            delete newData.pages[ii].comments[thinkingId]

            hasMutatedData = true;
            return newData;
          }
        }
      }

      return oldData;
    }
  )
  return hasMutatedData;
}

export const updateCommentInQueryData = (
  newComment: CommentType,
  queryClient: QueryClient,
  feedName: FeedNames,
) => {
  let hasMutatedData = false;
  queryClient.setQueryData(
    feedName,
    (oldData: FeedType) => {

      for (let ii = 0; ii < oldData.pages.length; ii++) {
        if (oldData.pages[ii].comments[newComment.docId]) {
          // found comment to update:
          if (enableDevTools) console.log('Updating Comment ', newComment.docId)
          let newData: FeedType = oldData;

          newData.pages[ii].comments[newComment.docId].comment = newComment.comment;

          hasMutatedData = true;
          return newData;
        }
      }

      return oldData;
    }
  )
  return hasMutatedData;
}

export const deleteCommentInQueryData = (
  commentId: string,
  queryClient: QueryClient,
  feedName: FeedNames,
) => {
  let hasMutatedData = false;
  queryClient.setQueryData(
    feedName,
    (oldData: FeedType) => {

      for (let ii = 0; ii < oldData.pages.length; ii++) {
        if (oldData.pages[ii].comments[commentId]){
          // Found comment to delete:
          if (enableDevTools) console.log('Deleting Comment ', commentId)
          let newData: FeedType = oldData;

          let comment = oldData.pages[ii].comments[commentId];
          if (comment.parent_reply_id) {
            filterListById(commentId, newData.pages[ii].comments[comment.parent_reply_id].flat_comments_list)
          } else if (comment.parent_comment_id) {
            filterListById(commentId, newData.pages[ii].comments[comment.parent_comment_id].flat_comments_list)
          } else if (comment.parent_post_id) {
            filterListById(commentId, newData.pages[ii].comments[comment.parent_post_id].flat_comments_list)
            if (oldData.pages[ii].orderedComments) {
              // should update orderedComments where necessary
              filterListById(commentId, newData.pages[ii].orderedComments)
            }
          }
          delete newData.pages[ii].comments[commentId];

          hasMutatedData = true;
          return newData;
        }
      }

      return oldData;
    }
  )
  return hasMutatedData;
}

export const editThinkingCommentInQueryData = (
  userId: string,
  thinkingData: CommentType & {parentId: string, userType: string},
  removing: boolean,
  queryClient: QueryClient,
  feedName: FeedNames,
) => {
  let hasMutatedData = false;
  queryClient.setQueryData(
    feedName,
    (oldData: FeedType) => {

      let parentId = thinkingData.parentId;
      if (thinkingData.level === 0) {
        // thinking about a post
        for (let ii = 0; ii < oldData.pages.length; ii++) {
          if (oldData.pages[ii].posts[parentId]) {
            // found post
            if (enableDevTools) console.log('Thinking about ', parentId)
            let newData: FeedType = oldData;
            let updatedPost = newData.pages[ii].posts[parentId];

            if (removing) {
              updatedPost.flat_comments_list ||= [];
              filterListById(`thinking-${userId}-${parentId}`, updatedPost.flat_comments_list)
              newData.pages[ii].posts[parentId] = updatedPost;
              delete newData.pages[ii].comments[`thinking-${userId}-${parentId}`]
              if (oldData.pages[ii].orderedComments) {
                // should update orderedComments where necessary
                filterListById(`thinking-${userId}-${parentId}`, newData.pages[ii].orderedComments)
              }
            } else {
              let newCommentData = {
                docId: `thinking-${userId}-${parentId}`,
                comment: [{
                  comment_body: 'Thinking...',
                  commenter_type: thinkingData.userType || 'user',
                  row_created: (new Date()).toString(),
                }],
                createdAt: (new Date()).toString(),
                level: 1,
                like_counts: 0,
                parent_comment_id: null,
                parent_post_id: parentId,
                reply_counts: 0,
                share_counts: 0,
                updatedAt: (new Date()).toString(),
                user_id: userId,
                watch: 0,
                isThinking: true,
                user_details: thinkingData.user_details
              }
              updatedPost.flat_comments_list ||= [];
              updatedPost.flat_comments_list.push(`thinking-${userId}-${parentId}`);
              newData.pages[ii].comments[`thinking-${userId}-${parentId}`] = newCommentData;
              if (oldData.pages[ii].orderedComments) {
                // should update orderedComments where necessary
                newData.pages[ii].orderedComments.push(`thinking-${userId}-${parentId}`)
              }
            }

            hasMutatedData = true;
            return newData;
          }
        }
      } else {
        // thinking about a comment
        for (let ii = 0; ii < oldData.pages.length; ii++) {
          if (oldData.pages[ii].comments[parentId]) {
            // found comment
            if (enableDevTools) console.log('Thinking about ', parentId)
            let newData: FeedType = oldData;
            let updatedComment = newData.pages[ii].comments[parentId];

            if (removing) {
              updatedComment.flat_comments_list ||= [];
              filterListById(`thinking-${userId}-parentId`, updatedComment.flat_comments_list)
              newData.pages[ii].comments[parentId] = updatedComment;
              delete newData.pages[ii].comments[`thinking-${userId}-${parentId}`]
            } else {
              let newCommentData = {
                docId: `thinking-${userId}-${parentId}`,
                comment: [{
                  comment_body: 'Thinking...',
                  commenter_type: thinkingData.userType || 'user',
                  row_created: (new Date()).toString(),
                }],
                createdAt: (new Date()).toString(),
                level: 1,
                like_counts: 0,
                parent_comment_id: null,
                parent_post_id: parentId,
                reply_counts: 0,
                share_counts: 0,
                updatedAt: (new Date()).toString(),
                user_id: userId,
                watch: 0,
                isThinking: true,
                user_details: thinkingData.user_details
              }
              updatedComment.flat_comments_list ||= [];
              updatedComment.flat_comments_list.push(`thinking-${userId}-${parentId}`);
              newData.pages[ii].comments[`thinking-${userId}-${parentId}`] = newCommentData;
            }

            hasMutatedData = true;
            return newData;
          }
        }
      }

      return oldData;
    }
  )
  return hasMutatedData;
}

//////////////////////////////// Meme Likes ////////////////////////////////////
export const editLikePostInQueryData = (
  docId: string,
  shouldAdd: boolean,
  queryClient: QueryClient,
  feedName: FeedNames,
  isAuthUser: boolean,
) => {
  let hasMutatedData = false;
  queryClient.setQueryData(
    feedName,
    (oldData: FeedType) => {

      for (let ii = 0; ii < oldData.pages.length; ii++) {
        if (oldData.pages[ii].posts[docId]) {
          // found post
          if (enableDevTools) console.log('Editing like ', docId)
          let newData: FeedType = oldData;

          if (shouldAdd) {
            newData.pages[ii].posts[docId].like_counts++;
            if (isAuthUser) newData.pages[ii].posts[docId].is_liked = true;
          } else {
            newData.pages[ii].posts[docId].like_counts--;
            if (isAuthUser) newData.pages[ii].posts[docId].is_liked = false;
          }

          hasMutatedData = true;
          return newData;
        }
      }

      return oldData;
    }
  )
  return hasMutatedData;
}

export const editLikeCommentInQueryData = (
  docId: string,
  shouldAdd: boolean,
  queryClient: QueryClient,
  feedName: FeedNames,
  isAuthUser: boolean,
) => {
  let hasMutatedData = false;
  queryClient.setQueryData(
    feedName,
    (oldData: FeedType) => {

      for (let ii = 0; ii < oldData.pages.length; ii++) {
        if (oldData.pages[ii].comments[docId]) {
          // found comment
          if (enableDevTools) console.log('Editing comment like ', docId)
          let newData: FeedType = oldData;

          if (shouldAdd) {
            newData.pages[ii].comments[docId].like_counts++;
            if (isAuthUser) newData.pages[ii].comments[docId].is_liked = true;
          } else {
            newData.pages[ii].comments[docId].like_counts--;
            if (isAuthUser) newData.pages[ii].comments[docId].is_liked = false;
          }

          hasMutatedData = true;
          return newData;
        }
      }

      return oldData;
    }
  )
  return hasMutatedData;
}
